import React, { useEffect } from 'react';
import { ArrowRight, ExternalLink, Users, Handshake, MessageCircle, Heart, Hotel, UtensilsCrossed, Wine, Megaphone, Gift, Newspaper, Camera, Share2, Image, UserCheck, BookOpen } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageSEO from '../components/PageSEO';
import './SponsorshipPage.css';

const JOTFORM_URL = "https://form.jotform.com/260186646305054";

const HERO_IMAGE = "https://static.prod-images.emergentagent.com/jobs/c38d8757-5ae8-45f5-8d20-0efa4695a7be/images/3af6d693f7cb1ee53f75eb271e46e4e7eec1a240b3adc30745f64090a2681e3a.png";

const WHY_PARTNER = [
  { num: "01", title: "Targeted Audience", desc: "Connect directly with HR executives, business owners, and organizational decision-makers actively seeking talent solutions.", icon: Users },
  { num: "02", title: "Brand Alignment", desc: "Associate your brand with workforce excellence, professional development, and community impact.", icon: Handshake },
  { num: "03", title: "Meaningful Networking", desc: "Build relationships in curated settings designed for substantive conversations, not just card exchanges.", icon: MessageCircle },
  { num: "04", title: "Community Impact", desc: "Support initiatives that strengthen local workforces and create economic opportunity.", icon: Heart },
];

const EVENT_STATS = [
  { num: "6", label: "Launch Events" },
  { num: "500+", label: "Expected Attendees" },
  { num: "85%", label: "Director-Level or Above" },
  { num: "12+", label: "Industries Represented" },
];

const SPONSORSHIP_CATEGORIES = [
  { num: "01", title: "Hotel Partner", desc: "Provide accommodations for speakers, VIP guests, and out-of-town attendees across our 6 launch events.", icon: Hotel },
  { num: "02", title: "Food & Catering", desc: "Fuel our events with exceptional cuisine \u2014 from networking receptions to executive luncheons.", icon: UtensilsCrossed },
  { num: "03", title: "Wine & Beverage", desc: "Elevate the experience with premium wine and beverage service at our networking events.", icon: Wine },
  { num: "04", title: "Marketing Partner", desc: "Support event promotion, digital campaigns, and brand visibility across all channels.", icon: Megaphone },
  { num: "05", title: "Swag & Gifts", desc: "Provide branded merchandise and gift items for attendee welcome bags and giveaways.", icon: Gift },
];

const SPONSOR_BENEFITS = [
  { title: "PR Inclusion", desc: "Featured in all press releases, media advisories, and official announcements.", icon: Newspaper },
  { title: "Press Coverage", desc: "Logo and brand inclusion in all media coverage and earned publicity.", icon: Camera },
  { title: "Social & Media", desc: "Recognition across all social media platforms and digital marketing campaigns.", icon: Share2 },
  { title: "Step & Repeat", desc: "Logo placement on event photo backdrop for maximum brand exposure.", icon: Image },
  { title: "Preferred Partner", desc: "Recognition as a preferred partner with access to our membership network.", icon: UserCheck },
  { title: "Membership Access", desc: "Inclusion in our partner directory shared with all R6\u2122 Collective members.", icon: BookOpen },
];

const SponsorshipPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="sk-page" data-testid="sponsorship-page">
      <PageSEO
        title="Become a Sponsor"
        path="/sponsorship"
        description="Partner with The R6 Collective on our 6 Launch Events series. Connect your brand with HR leaders, business executives, and community partners driving workforce transformation."
      />
      <Header />

      {/* ─── HERO ─── */}
      <section className="sk-hero" data-testid="sk-hero">
        <div className="sk-hero-grid">
          <div className="sk-hero-content">
            <span className="sk-label-hero">6 Launch Events</span>
            <h1 className="sk-hero-title">Become a Sponsor.</h1>
            <p className="sk-hero-sub">
              Partner with The R6&#8482; Collective on our 6 Launch Events series. Connect your brand with HR leaders,
              business executives, and community partners driving workforce transformation.
            </p>
            <div className="sk-hero-ctas">
              <a href={JOTFORM_URL} target="_blank" rel="noopener noreferrer" className="sk-btn sk-btn-gold" data-testid="sk-inquire-hero">
                Inquire for Event Specifics <ExternalLink size={14} />
              </a>
              <a href="#categories" className="sk-btn sk-btn-outline" data-testid="sk-see-categories">
                See Sponsorship Categories
              </a>
            </div>
          </div>
          <div className="sk-hero-image">
            <img src={HERO_IMAGE} alt="R6 Collective Conference registration with diverse professionals" loading="eager" />
            <span className="sk-hero-caption">Join us at our signature launch events</span>
          </div>
        </div>
      </section>

      {/* ─── WHY PARTNER ─── */}
      <section className="sk-section" data-testid="sk-why-partner">
        <div className="sk-container">
          <span className="sk-label">Why Partner</span>
          <h2 className="sk-title">Why partner with the R6&#8482; Collective?</h2>
          <div className="sk-why-grid">
            {WHY_PARTNER.map(w => (
              <div key={w.num} className="sk-why-card" data-testid={`why-${w.num}`}>
                <div className="sk-why-icon"><w.icon size={22} /></div>
                <span className="sk-why-num">{w.num}</span>
                <h3>{w.title}</h3>
                <p>{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── EVENT REACH ─── */}
      <section className="sk-section sk-section-stats" data-testid="sk-event-reach">
        <div className="sk-container">
          <span className="sk-label sk-label-light">Event Reach</span>
          <h2 className="sk-title sk-title-white">Connect with decision-makers who shape organizational talent strategy.</h2>
          <div className="sk-stats-grid">
            {EVENT_STATS.map((s, i) => (
              <div key={i} className="sk-stat" data-testid={`stat-${i}`}>
                <span className="sk-stat-num">{s.num}</span>
                <span className="sk-stat-label">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── SPONSORSHIP CATEGORIES ─── */}
      <section className="sk-section sk-section-alt" id="categories" data-testid="sk-categories">
        <div className="sk-container">
          <span className="sk-label">Sponsorship Categories</span>
          <h2 className="sk-title">Multiple ways to align your brand with our launch events.</h2>
          <div className="sk-cat-grid">
            {SPONSORSHIP_CATEGORIES.map(c => (
              <div key={c.num} className="sk-cat-card" data-testid={`cat-${c.num}`}>
                <div className="sk-cat-top">
                  <span className="sk-cat-num">{c.num}</span>
                  <c.icon size={22} className="sk-cat-icon" />
                </div>
                <h3>{c.title}</h3>
                <p>{c.desc}</p>
                <a href={JOTFORM_URL} target="_blank" rel="noopener noreferrer" className="sk-cat-link" data-testid={`inquire-${c.num}`}>
                  Inquire Now <ArrowRight size={14} />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── SPONSOR BENEFITS ─── */}
      <section className="sk-section" data-testid="sk-benefits">
        <div className="sk-container">
          <span className="sk-label">Sponsor Benefits</span>
          <h2 className="sk-title">What you receive as an event sponsor.</h2>
          <div className="sk-benefits-grid">
            {SPONSOR_BENEFITS.map((b, i) => (
              <div key={i} className="sk-benefit-card" data-testid={`benefit-${i}`}>
                <b.icon size={20} className="sk-benefit-icon" />
                <h4>{b.title}</h4>
                <p>{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="sk-section sk-section-cta" data-testid="sk-cta">
        <div className="sk-container sk-cta-inner">
          <span className="sk-label sk-label-light">Inquire for Event Specifics</span>
          <h2>Each of our 6 launch events offers unique sponsorship opportunities.</h2>
          <p>
            Contact us to learn about specific event dates, locations, expected attendance, and custom partnership
            packages designed around your goals.
          </p>
          <a href={JOTFORM_URL} target="_blank" rel="noopener noreferrer" className="sk-btn sk-btn-gold" data-testid="sk-cta-btn">
            Get Event Details <ExternalLink size={14} />
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SponsorshipPage;
